import { useEffect, useMemo } from 'react';

import type { InputSearchOption } from '@snowit/ui/Inputs/InputSearch/InputSearchV2';

import { NULL_KEY } from '@snowit/constants';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import usePartners from '@snowit/system/usePartners';
import useTenants from '@snowit/system/useTenants';
import InputSearch from '@snowit/ui/Inputs/InputSearch/InputSearchV2';
import PERMISSIONS from '@snowit/utilities/permissions';

interface PartnerGlobalPickerProps {
  onSelect?: (input: string) => void;
  small?: boolean;
}

const PartnerGlobalPicker = (props: PartnerGlobalPickerProps) => {
  const { t } = useLanguage();
  const { activeTenants: tenants, hasTenants } = useTenants();
  const { partners, selectPartner, partner: selected } = usePartners();
  const { authType, hasPermission, claims } = useAuth();

  const handleSelect = (partner: string) => {
    const val = partner === NULL_KEY ? undefined : partner;
    selectPartner(val);
    if (typeof props.onSelect === 'function') {
      props.onSelect(partner);
    }
  };

  const options = useMemo(() => {
    return partners
      .filter((partner) => partner.name && partner.uid)
      .filter((partner) => {
        if (!tenants?.length) return true;
        return hasTenants(partner.tenants);
      })
      .filter((partner) => {
        if (authType === 'ADMIN') return true;
        if (hasPermission(PERMISSIONS.SELECTORS__PARTNERS_VIEW_ALL)) return true;
        return (claims.partnerUIDS ?? []).includes(partner.uid);
      })
      .map((partner) => ({
        label: partner.name,
        hint: partner.uid,
        key: partner.uid,
        value: partner.uid,
        search: partner.name,
      }))
      .sort((a, b) => {
        if (authType === 'PARTNER') {
          return (
            (claims.partnerUIDS ?? []).indexOf(a.key) - (claims.partnerUIDS ?? []).indexOf(b.key)
          );
        }
        return a.label.localeCompare(b.label);
      });
  }, [claims, JSON.stringify(partners), tenants]);

  const shouldAutoSelect = useMemo(() => {
    if (authType === 'ADMIN') return false;
    if (options.length === 1) return true;
    if (hasPermission(PERMISSIONS.SELECTORS__PARTNERS_SELECT_ALL)) {
      return false;
    }
    if (options.length >= 1 && !selected && authType === 'PARTNER') {
      return true;
    }
    return false;
  }, [authType, JSON.stringify(options), selected]);

  useEffect(() => {
    if (!options.length) return;
    const has = options.some((opt) => opt.value === selected);
    if (!has) {
      if (shouldAutoSelect) {
        selectPartner(options[0].key);
      } else {
        selectPartner(undefined);
      }
    }
  }, [JSON.stringify(options), shouldAutoSelect]);

  if (options.length === 0) return null;

  const optionsAll: InputSearchOption<any>[] = hasPermission(
    PERMISSIONS.SELECTORS__PARTNERS_SELECT_ALL,
  )
    ? [
        {
          key: NULL_KEY,
          label: t('lzy.const.partners_all'),
          value: NULL_KEY,
          search: 'all',
        },
      ]
    : [];

  if (authType !== 'ADMIN' && hasPermission(PERMISSIONS.SELECTORS__PARTNERS_HIDE)) {
    return null;
  }

  return (
    <InputSearch
      // inputClassName={s.select}
      options={[...optionsAll, ...options]}
      placeholder={t('lzy.input.placeholder.partners_all')}
      onSelect={(args) => handleSelect(args.value)}
      value={selected}
      readOnly={options.length === 1}
      size={props.small ? 'db' : undefined}
      resettable={authType === 'ADMIN'}
    />
  );
};

export default PartnerGlobalPicker;
